/**
 * « À traiter » : UNE file pour tout ce qui attend quelqu'un, du plus urgent au
 * moins urgent (glossaire : docs/design/SYSTEME.md). Elle remplace la file des
 * accueils patron et dispatcher, leurs cartes (absences, tâches, documents,
 * objectifs, « À surveiller ») et les rappels des « bons réflexes ».
 *
 * Chaque ligne dit quoi, pourquoi et depuis quand, et ouvre l'enregistrement
 * exact (cette intervention, cette facture, cette demande), jamais une liste.
 * Calcul pur, testé à part (tests/unit/home-queue.test.ts).
 */
import { approxClock } from '@myequip/shared';
import { MATERIEL_PATHS } from '../../app/materiel-sections.js';
import { dateFr, euros, heure } from '../../core/format.js';
import { localDay } from './local-day.js';
import type { Exceptions, ForgottenEnd, MyDay, ObjectivePerson, Reminder } from './home-data.js';

export type Severity = 'critique' | 'haute' | 'moyenne' | 'basse';

export type QueueKind =
  | 'urgence' | 'sla' | 'demande' | 'retard' | 'oubli' | 'non_affectee' | 'conflit' | 'sync'
  | 'absence' | 'objectif' | 'tache' | 'facture' | 'devis' | 'rapport' | 'conformite'
  | 'document' | 'annonce' | 'rappel' | 'stock' | 'anomalie';

/** Geste possible depuis la ligne, avec ses états affichés par l'écran. */
export type QueueAct =
  | { kind: 'prise_en_charge'; alertId: string }
  | { kind: 'absence'; requestId: string; name: string }
  | { kind: 'plus_tard'; reminderId: string };

export interface QueueItem {
  /** Identité stable : famille et identifiant. */
  key: string;
  kind: QueueKind;
  severity: Severity;
  /** Quoi. */
  title: string;
  /** Lequel, et pourquoi. */
  detail: string;
  /** Depuis quand (ou pour quand) : sert au tri, à gravité égale. */
  since?: string;
  sinceLabel?: string;
  /** L'enregistrement exact. */
  to: string;
  act?: QueueAct;
}

export interface AbsenceLike {
  id: string;
  kind: string;
  user_name?: string;
  starts_at: string;
  ends_at: string;
}

export interface DocumentLike {
  id: string;
  title: string;
  archivedAt: string | null;
  myStatus: string;
  version: { publishedAt: string };
}

/** Annonce de l'entreprise à lire ou à confirmer (`GET /announcements`, liste `pinned`). */
export interface AnnouncementLike {
  id: string;
  title: string;
  authorName: string | null;
  publishedAt: string;
  urgent: boolean;
  requiresAck: boolean;
  archivedAt: string | null;
  mine: { seenAt: string | null; acknowledgedAt: string | null } | null;
  pending: boolean;
}

export interface TaskLike {
  id: string;
  title: string;
  status: string;
  due_on: string | null;
  due_at: string | null;
  created_by: string;
  assignee_id: string;
  created_by_name: string;
}

export interface QueueSources {
  /** Vue terrain : sa journée ; sinon le bureau. */
  field: boolean;
  exceptions?: Exceptions | undefined;
  myDay?: MyDay | undefined;
  reminders?: Reminder[] | undefined;
  /** Rappels reportés à demain sur cet appareil. */
  hiddenReminders?: readonly string[];
  absences?: AbsenceLike[] | undefined;
  /** Libellé lisible d'une demande d'absence (type et période). */
  absenceLabel?: (absence: AbsenceLike) => string;
  documents?: DocumentLike[] | undefined;
  /** Annonces à lire ou à confirmer (rubrique Messages). */
  announcements?: AnnouncementLike[] | undefined;
  /** Tâches ouvertes de la personne (bureau seulement : au terrain, elles sont dans sa journée). */
  tasks?: TaskLike[] | undefined;
  objectives?: ObjectivePerson[] | undefined;
  typeLabel?: (type: string) => string;
}

const RANK: Record<Severity, number> = { critique: 0, haute: 1, moyenne: 2, basse: 3 };

/** « depuis 08:05 », « depuis hier », « depuis le 12/10/2026 ». */
export function sinceText(at: string, now: Date, prefix = 'depuis'): string {
  const day = localDay(at);
  const today = localDay(now);
  if (day === today) return `${prefix} ${heure(at)}`;
  const yesterday = localDay(new Date(now.getTime() - 86_400_000));
  if (day === yesterday) return `${prefix} hier`;
  return `${prefix} le ${dateFr(at)}`;
}

/** Le plus ancien d'abord ; une ligne sans date passe après celles qui en ont une. */
function bySince(a: string | undefined, b: string | undefined): number {
  if (a === undefined || b === undefined) return a === b ? 0 : a === undefined ? 1 : -1;
  return a.localeCompare(b);
}

/** Ordre de la file : gravité, puis le plus ancien (ou la plus proche échéance) ; une ligne par dossier. */
export function orderQueue(items: QueueItem[]): QueueItem[] {
  const sorted = [...items].sort((a, b) =>
    RANK[a.severity] - RANK[b.severity]
    || bySince(a.since, b.since));
  const records = new Set<string>();
  return sorted.filter((item) => {
    // Un même dossier (intervention, facture, devis…) ne revient pas deux fois : la ligne la plus grave reste.
    if (!/^\/(interventions|factures|devis|urgences|equipements|contrats)\/[^/?]+$/.test(item.to)) return true;
    if (records.has(item.to)) return false;
    records.add(item.to);
    return true;
  });
}

function forgotten(oubli: ForgottenEnd, field: boolean): QueueItem {
  const where = `${oubli.customer_name} — ${oubli.site_label}`;
  const stop = oubli.segment === 'pause'
    ? `en pause depuis ${heure(oubli.segment_started_at)}`
    : `compteur arrêté à ${heure(oubli.stopped_at)}`;
  return {
    key: `oubli:${oubli.id}`,
    kind: 'oubli',
    severity: 'haute',
    title: field
      ? 'Vous avez oublié de terminer ?'
      : `Intervention restée ouverte${oubli.technician ? ` — ${oubli.technician}` : ''}`,
    detail: `${where} · ${stop}`,
    since: oubli.stopped_at,
    sinceLabel: 'Rien de nouveau depuis : à terminer ou à reprendre',
    to: `/interventions/${oubli.intervention_id}`,
  };
}

const SLA_KINDS: Record<string, string> = {
  acquittement: 'accusé de réception',
  prise_en_charge: 'prise en charge',
  resolution: 'résolution',
};

const ANOMALY_KINDS: Record<string, string> = {
  consommation_provisoire: 'consommation provisoire',
  stock_negatif: 'stock négatif',
  correction_ocr: 'correction de facture lue',
  ecart_inventaire: 'écart d’inventaire',
};

/** La file complète, triée et sans doublon. */
export function buildQueue(sources: QueueSources, now = new Date()): QueueItem[] {
  const items: QueueItem[] = [];
  const type = sources.typeLabel ?? ((value: string) => value);
  const today = localDay(now);
  const ex = sources.exceptions;
  const alerts = sources.field ? sources.myDay?.urgences ?? [] : ex?.urgences ?? [];

  for (const alert of alerts) {
    const open = alert.status === 'declenchee' || alert.status === 'escaladee';
    items.push({
      key: `urgence:${alert.id}`,
      kind: 'urgence',
      severity: open ? 'critique' : 'haute',
      title: open ? 'Urgence à prendre en charge' : 'Urgence prise en charge, à résoudre',
      detail: alert.title,
      ...(alert.created_at ? { since: alert.created_at, sinceLabel: sinceText(alert.created_at, now, 'Déclenchée') } : {}),
      to: `/urgences/${alert.id}`,
      // Pendant une astreinte (19/10/2026), seuls l'astreinte et le bureau la prennent en charge :
      // les autres ouvrent la fiche, qui dit qui appeler.
      ...(open && alert.can_handle !== false ? { act: { kind: 'prise_en_charge' as const, alertId: alert.id } } : {}),
    });
  }

  if (sources.field) {
    for (const oubli of sources.myDay?.oublis ?? []) items.push(forgotten(oubli, true));
    for (const report of sources.myDay?.incomplets ?? []) {
      items.push({
        key: `rapport:${report.id}`,
        kind: 'rapport',
        severity: 'moyenne',
        title: 'Rapport à terminer',
        detail: report.customer_name ? `${report.customer_name} — ${report.site_label ?? ''}` : 'Rapport en brouillon à compléter',
        ...(report.starts_at ? { since: report.starts_at, sinceLabel: sinceText(report.starts_at, now, 'Intervention commencée') } : {}),
        to: `/interventions/${report.intervention_id}`,
      });
    }
  }

  if (ex && !sources.field) {
    for (const sla of ex.slaAlertes) {
      items.push({
        key: `sla:${sla.id}`,
        kind: 'sla',
        severity: 'critique',
        title: 'Délai du contrat à tenir',
        detail: `${sla.contract_label ?? 'Contrat'} · ${SLA_KINDS[sla.kind] ?? sla.kind}`,
        since: sla.due_at,
        sinceLabel: `Avant le ${dateFr(sla.due_at)} à ${heure(sla.due_at)}`,
        to: sla.contract_id ? `/contrats/${sla.contract_id}` : sla.intervention_id ? `/interventions/${sla.intervention_id}` : '/contrats',
      });
    }
    for (const request of ex.demandesAQualifier ?? []) {
      const urgent = request.perceived_urgency === 'critique' || request.perceived_urgency === 'haute';
      items.push({
        key: `demande:${request.id}`,
        kind: 'demande',
        severity: urgent ? 'critique' : 'haute',
        title: urgent ? 'Demande urgente à qualifier' : 'Demande à qualifier',
        // Le client d'abord (ce qu'on reconnaît), la référence en dernier.
        detail: `${request.customer_name ?? request.requester_name}`
          + (request.site_label ? ` · ${request.site_label}` : '') + ` · ${request.reference}`,
        since: request.created_at,
        sinceLabel: sinceText(request.created_at, now, 'Reçue'),
        to: `/interventions/demandes/${request.id}`,
      });
    }
    for (const late of ex.retards ?? []) {
      items.push({
        key: `retard:${late.id}`,
        kind: 'retard',
        severity: 'haute',
        title: `${late.assignee_name} en retard`,
        detail: `${late.customer_name} — ${late.site_label} · ${type(late.type)}`,
        since: late.starts_at,
        sinceLabel: `Prévue ${approxClock(late.starts_at, late.time_approx_minutes)}, pas encore partie`,
        to: `/interventions/${late.id}`,
      });
    }
    for (const oubli of ex.oublis ?? []) items.push(forgotten(oubli, false));
    for (const visit of ex.nonAffectees ?? []) {
      items.push({
        key: `non_affectee:${visit.id}`,
        kind: 'non_affectee',
        severity: 'haute',
        title: 'Intervention sans technicien',
        detail: `${visit.customer_name} — ${visit.site_label} · ${type(visit.type)}`,
        since: visit.starts_at,
        sinceLabel: `Aujourd’hui ${approxClock(visit.starts_at, visit.time_approx_minutes)}`,
        to: `/interventions/${visit.id}`,
      });
    }
    for (const conflict of ex.conflits) {
      const intervention = conflict.item_a_kind === 'intervention' ? conflict.item_a_id
        : conflict.item_b_kind === 'intervention' ? conflict.item_b_id : undefined;
      items.push({
        key: `conflit:${conflict.id}`,
        kind: 'conflit',
        severity: 'haute',
        title: `Conflit de planning — ${conflict.full_name}`,
        detail: 'Deux créneaux se chevauchent',
        ...(conflict.detected_at ? { since: conflict.detected_at, sinceLabel: sinceText(conflict.detected_at, now, 'Repéré') } : {}),
        to: intervention ? `/interventions/${intervention}` : '/planning',
      });
    }
    for (const conflict of ex.syncConflicts) {
      items.push({
        key: `sync:${conflict.id}`,
        kind: 'sync',
        severity: 'haute',
        title: 'Conflit de synchronisation à arbitrer',
        detail: conflict.field ? `Champ « ${conflict.field} » modifié hors ligne et au bureau` : 'Modifié hors ligne et au bureau',
        ...(conflict.created_at ? { since: conflict.created_at, sinceLabel: sinceText(conflict.created_at, now) } : {}),
        to: '/conflits',
      });
    }
    for (const invoice of ex.impayes) {
      const due = Number(invoice.reste_a_payer_cents ?? invoice.total_ttc_cents);
      items.push({
        key: `facture:${invoice.id}`,
        kind: 'facture',
        severity: 'moyenne',
        title: 'Facture en retard de paiement',
        detail: `${invoice.display_name} · ${euros(due)} restant dû · ${invoice.number}`,
        ...(invoice.due_date ? { since: invoice.due_date, sinceLabel: `Échue le ${dateFr(invoice.due_date)}` } : {}),
        to: `/factures/${invoice.id}`,
      });
    }
    for (const quote of ex.devisEnAttente ?? []) {
      items.push({
        key: `devis:${quote.id}`,
        kind: 'devis',
        severity: 'moyenne',
        title: 'Devis sans réponse',
        detail: `${quote.display_name} · ${quote.number}`,
        since: quote.sent_at,
        sinceLabel: `Envoyé le ${dateFr(quote.sent_at)}`,
        to: `/devis/${quote.id}`,
      });
    }
    for (const report of ex.rapportsInacheves ?? []) {
      items.push({
        key: `rapport:${report.id}`,
        kind: 'rapport',
        severity: 'moyenne',
        title: 'Rapport à terminer',
        detail: report.display_name,
        ...(report.starts_at ? { since: report.starts_at, sinceLabel: sinceText(report.starts_at, now, 'Intervention commencée') } : {}),
        to: `/interventions/${report.id}`,
      });
    }
    for (const machine of ex.conformite) {
      const overdue = machine.next_leak_check_on.slice(0, 10) < today;
      items.push({
        key: `conformite:${machine.id}`,
        kind: 'conformite',
        severity: overdue ? 'haute' : 'moyenne',
        title: 'Contrôle d’étanchéité à planifier',
        detail: `${machine.customer_name}${machine.serial_number ? ` · ${machine.serial_number}` : ''}`,
        since: machine.next_leak_check_on,
        sinceLabel: `${overdue ? 'Dépassé depuis le' : 'Avant le'} ${dateFr(machine.next_leak_check_on)}`,
        to: `/equipements/${machine.id}`,
      });
    }
    for (const article of ex.stockBas) {
      items.push({
        key: `stock:${article.id}`,
        kind: 'stock',
        severity: 'basse',
        title: 'Stock sous le seuil',
        detail: `${article.reference} — ${article.label}`,
        to: `/stock/articles/${article.id}`,
      });
    }
    for (const anomaly of ex.anomalies) {
      items.push({
        key: `anomalie:${anomaly.id}`,
        kind: 'anomalie',
        severity: 'basse',
        title: 'Écart de stock à régulariser',
        detail: `${anomaly.reference} — ${ANOMALY_KINDS[anomaly.kind] ?? anomaly.kind}`,
        ...(anomaly.created_at ? { since: anomaly.created_at, sinceLabel: sinceText(anomaly.created_at, now) } : {}),
        to: anomaly.article_id ? `/stock/articles/${anomaly.article_id}` : MATERIEL_PATHS.surveiller,
      });
    }
  }

  if (!sources.field) {
    for (const absence of sources.absences ?? []) {
      const name = absence.user_name ?? '';
      items.push({
        key: `absence:${absence.id}`,
        kind: 'absence',
        severity: 'moyenne',
        title: `Absence à valider — ${name}`,
        detail: sources.absenceLabel?.(absence) ?? absence.kind,
        since: absence.starts_at,
        to: `/equipe/decisions?id=${absence.id}`,
        act: { kind: 'absence', requestId: absence.id, name },
      });
    }
    for (const person of sources.objectives ?? []) {
      for (const objective of person.items) {
        if (objective.state !== 'bloque' && objective.state !== 'non_atteint') continue;
        const blocked = objective.state === 'bloque';
        items.push({
          key: `objectif:${objective.id}`,
          kind: 'objectif',
          severity: 'moyenne',
          title: `Objectif ${blocked ? 'bloqué' : 'non atteint'} — ${person.name}`,
          detail: `${objective.title}${blocked && objective.blocked_reason ? ` · ${objective.blocked_reason}` : ''}`,
          since: objective.due_at,
          sinceLabel: `Pour ${heure(objective.due_at)}`,
          to: `/taches?onglet=equipe&id=${objective.id}`,
        });
      }
    }
    for (const task of sources.tasks ?? []) {
      if (task.status !== 'a_faire') continue;
      const late = task.due_on !== null && task.due_on < today;
      const dueToday = task.due_on === today || (task.due_at !== null && localDay(task.due_at) === today);
      items.push({
        key: `tache:${task.id}`,
        kind: 'tache',
        severity: late || dueToday ? 'moyenne' : 'basse',
        title: task.title,
        detail: task.created_by !== task.assignee_id ? `Tâche donnée par ${task.created_by_name}` : 'Ma tâche',
        ...(task.due_at
          ? { since: task.due_at, sinceLabel: `Objectif pour ${localDay(task.due_at) === today ? heure(task.due_at) : dateFr(task.due_at)}` }
          : task.due_on
            ? { since: task.due_on, sinceLabel: late ? `En retard — prévue le ${dateFr(task.due_on)}` : dueToday ? 'Pour aujourd’hui' : `Pour le ${dateFr(task.due_on)}` }
            : {}),
        to: `/taches?id=${task.id}`,
      });
    }
  }

  for (const document of sources.documents ?? []) {
    if (document.archivedAt || (document.myStatus !== 'a_lire' && document.myStatus !== 'nouvelle_version')) continue;
    items.push({
      key: `document:${document.id}`,
      kind: 'document',
      severity: 'basse',
      title: `${document.myStatus === 'nouvelle_version' ? 'Nouvelle version à lire' : 'Document à lire'} : ${document.title}`,
      detail: 'Quelques minutes, puis « J’ai lu ».',
      since: document.version.publishedAt,
      sinceLabel: `Publié le ${dateFr(document.version.publishedAt)}`,
      to: `/entreprise/documents/${document.id}`,
    });
  }

  // Annonce de l'entreprise (25/10/2026) : en tête tant qu'elle n'est pas vue, ou pas confirmée quand elle le demande.
  for (const announcement of sources.announcements ?? []) {
    if (!announcement.pending || announcement.archivedAt || !announcement.mine) continue;
    const toConfirm = announcement.requiresAck && announcement.mine.seenAt !== null;
    items.push({
      key: `annonce:${announcement.id}`,
      kind: 'annonce',
      severity: 'haute',
      title: `${announcement.urgent ? 'Annonce urgente' : toConfirm ? 'Annonce à confirmer' : 'Annonce à lire'} : ${announcement.title}`,
      detail: [
        announcement.authorName ? `De ${announcement.authorName}` : null,
        announcement.requiresAck ? 'Lisez-la, puis « J’ai pris connaissance ».' : 'Quelques secondes de lecture.',
      ].filter(Boolean).join(' · '),
      since: announcement.publishedAt,
      sinceLabel: `Publiée le ${dateFr(announcement.publishedAt)}`,
      to: `/messages/annonces/${announcement.id}`,
    });
  }

  const hidden = new Set(sources.hiddenReminders ?? []);
  for (const reminder of sources.reminders ?? []) {
    if (hidden.has(reminder.id)) continue;
    items.push({
      key: `rappel:${reminder.id}`,
      kind: 'rappel',
      severity: 'basse',
      title: reminder.title,
      detail: reminder.detail,
      to: reminder.to,
      act: { kind: 'plus_tard', reminderId: reminder.id },
    });
  }

  return orderQueue(items);
}

/** Familles qui méritent la carte « Maintenant » du bureau, en plus de tout ce qui est critique. */
const NOW_KINDS = new Set<QueueKind>(['urgence', 'demande', 'retard', 'oubli']);

/** Ce que le bureau doit regarder tout de suite, s'il y a quelque chose. */
export function pickNow(items: readonly QueueItem[]): QueueItem | undefined {
  const first = items[0];
  if (!first) return undefined;
  return first.severity === 'critique' || (first.severity === 'haute' && NOW_KINDS.has(first.kind)) ? first : undefined;
}

/**
 * La file sans ce que « Maintenant » montre déjà : la ligne mise en avant au bureau ;
 * au terrain, le rapport de la visite en cours (« Continuer » l'ouvre), qu'il vienne de
 * la journée ou des rappels du jour. Un oubli de fin ou une urgence de cette visite reste.
 */
export function withoutNow(items: readonly QueueItem[], now: { key?: string | undefined; visit?: string | undefined }): QueueItem[] {
  return items.filter((item) => item.key !== now.key
    && !((item.kind === 'rapport' || item.kind === 'rappel') && now.visit !== undefined && item.to === now.visit));
}

/** Liste complète de chaque famille, pour « Tout voir ». */
export const KIND_LIST: Record<QueueKind, { to: string; many: string }> = {
  urgence: { to: '/planning', many: 'urgences' },
  sla: { to: '/contrats', many: 'délais de contrat' },
  demande: { to: '/demandes', many: 'demandes à qualifier' },
  retard: { to: '/planning', many: 'retards' },
  oubli: { to: '/planning', many: 'interventions restées ouvertes' },
  non_affectee: { to: '/planning', many: 'interventions sans technicien' },
  conflit: { to: '/planning', many: 'conflits de planning' },
  sync: { to: '/conflits', many: 'conflits de synchronisation' },
  absence: { to: '/equipe/decisions', many: 'absences à valider' },
  objectif: { to: '/taches?onglet=equipe', many: 'objectifs à revoir' },
  tache: { to: '/taches', many: 'tâches' },
  facture: { to: '/factures', many: 'factures en retard' },
  devis: { to: '/devis?etat=envoye', many: 'devis sans réponse' },
  rapport: { to: '/planning', many: 'rapports à terminer' },
  conformite: { to: '/fluides', many: 'contrôles à planifier' },
  document: { to: '/entreprise/documents', many: 'documents à lire' },
  annonce: { to: '/messages?onglet=annonces', many: 'annonces à lire' },
  rappel: { to: MATERIEL_PATHS.stock, many: 'rappels' },
  stock: { to: MATERIEL_PATHS.surveiller, many: 'articles sous le seuil' },
  anomalie: { to: MATERIEL_PATHS.surveiller, many: 'écarts de stock' },
};

/** Taille des listes de l'accueil côté serveur : une famille qui l'atteint a peut-être une suite. */
export const SERVER_LIST_BOUND = 10;

/**
 * Lignes montrées d'emblée : toutes les critiques, puis les suivantes jusqu'à
 * `limit`. Les autres restent à un geste (« Voir les N autres »). Une famille
 * dont la liste du serveur est pleine renvoie aussi vers sa liste complète.
 */
export function splitQueue(items: readonly QueueItem[], limit: number): {
  shown: QueueItem[];
  hidden: QueueItem[];
  lists: { kind: QueueKind; to: string; label: string }[];
} {
  const critical = items.filter((item) => item.severity === 'critique').length;
  const shown = items.slice(0, Math.max(limit, critical));
  const counts = new Map<QueueKind, number>();
  for (const item of items) counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1);
  const lists = [...counts.entries()]
    .filter(([kind, count]) => count >= SERVER_LIST_BOUND && kind !== 'rappel')
    .map(([kind]) => {
      const many = KIND_LIST[kind].many;
      return { kind, to: KIND_LIST[kind].to, label: `${many.charAt(0).toUpperCase()}${many.slice(1)} : la liste complète` };
    });
  return { shown, hidden: items.slice(shown.length), lists };
}
